/**
 * carriers/ohio — Ohio carrier (CVN-10)
 * @specialization 다단 파상 타격 집행자 — TaskRef 기반 실행 전담 · 계획 이탈 금지
 *
 * Ohio carrier를 프레임워크에 등록합니다.
 */

import type { CarrierMetadata, CarrierPersonaDefaults } from "../dispatch/types.js";

import { CARRIER_JOBS_SELF_CALL_HINT } from "../constants.js";

export const OHIO_DEFAULTS: CarrierPersonaDefaults = {
  id: "ohio",
  displayName: "Ohio",
  slot: 4,
  agent: {
    dispatch: {
      defaultCliType: "claude",
      defaultModel: "sonnet",
      defaultEffort: "low",
    },
  },
};

export const CARRIER_METADATA: CarrierMetadata = {
  // ── Tier 1: Routing ──
  title: "Multi-Wave Execution",
  summary: "Executes same-Lane TaskRefs and marks them complete only after Lane QA.",
  category: "planning",
  whenToUse: [
    "multi-wave builds from same-Lane TaskRefs",
    "PRD-driven implementations with structured execution waves",
    "refactors or migrations with ≥4 dependent steps",
    "cross-module coordinated changes following a Kirov plan",
  ],
  whenNotToUse: [
    "single-file edits or host-directed single-shot tasks (→genesis)",
    "architecture decisions (→nimitz)",
    "planning work itself (→kirov)",
    "reconnaissance before planning (→vanguard/tempest)",
  ],
  requestBlocks: [
    { tag: "task_refs", hint: "Required newline- or comma-delimited fully qualified TaskRefs from exactly one Plan and one Lane. Ohio calls plan_read once at dispatch start with the complete set and executes only the returned selected_tasks.", required: true },
    { tag: "objective", hint: "Optional brief restatement of the overarching goal for context anchoring.", required: false },
    { tag: "scope", hint: "Optional explicit boundaries that further narrow, but never expand, the assigned TaskRefs.", required: false },
    { tag: "constraints", hint: "Optional hard constraints, deadlines, or compatibility requirements that override or supplement the plan.", required: false },
  ],
  allowedExecutorTools: ["carrier_jobs", "plan_read", "plan_mark_tasks"],

  // ── Tier 2: Composition ──
  permissions: [
    "Full access to the codebase — read, write, and execute commands.",
    "MUST call plan_read exactly once at the start of each dispatch with the complete assigned TaskRef set. Re-read only after a Plan tool reports a Plan-state conflict or the host explicitly redirects; invalid, missing, cross-Plan, or cross-Lane TaskRefs are blockers.",
    "May change only files in the resolved Lane's exact write set and execute only the assigned TaskRefs. MUST NOT execute unassigned tasks or another Lane, expand scope, or guess an ambiguous assignment.",
    "MUST treat the host agent's <objective>, <scope>, and <constraints> as binding ALONGSIDE the resolved Plan contract. Even if a step or constraint seems suboptimal, MUST NOT substitute autonomous design judgment.",
    "MUST call plan_mark_tasks with exactly the assigned TaskRefs only after every assigned task and the Lane QA/integration gate pass. Never edit Plan Markdown or checkbox state through filesystem tools.",
    "MUST NOT silently re-plan, skip steps, invent new workflow paths, or expand scope beyond what the resolved Plan contract specifies.",
    "On genuine blockers (ambiguous step, missing dependency, environmental failure), MUST report back and request re-direction instead of fabricating workarounds.",
  ],
  principles: [
    CARRIER_JOBS_SELF_CALL_HINT,
    "Treat compact plan_context as the forest: its Objective, topology, current progress, global QA gates, acceptance criteria, documentation updates, and final review loop govern the mission. Treat lane_context and selected_tasks as the only executable scope and write authority.",
    "Use the one dispatch-start plan_read result as the binding execution contract; do not repeatedly read an unchanged Plan, deviate, re-plan, or skip assigned tasks.",
    "Reject missing, malformed, invalid-Plan, cross-Plan, or cross-Lane TaskRefs instead of guessing or inventing replacement work.",
    "Change only the resolved Lane's exact write set. Before execution, satisfy its dependency/start condition and predecessor gates; after execution, satisfy its QA/integration gate before calling plan_mark_tasks.",
    "Execute waves in the declared order; preserve QA checkpoints between waves and do not collapse them.",
    "After the Lane QA/integration gate passes, call plan_mark_tasks exactly once with the assigned TaskRefs. The tool owns checkbox mutation; never use filesystem tools to modify Plan state.",
    "If a wave or constraint should be redesigned, MUST escalate to the host agent via the completion report instead of silently altering the wave's intent.",
    "Escalate genuine blockers (ambiguous step, missing dependency, environmental failure) instead of fabricating workarounds.",
    "Do not absorb planning, architecture, or QA roles — if the plan demands a decision Ohio cannot make, escalate to the appropriate carrier (Kirov/Nimitz/Sentinel).",
  ],
  outputFormat:
    `After completing the assigned wave(s), provide a structured wave-completion report.\n` +
    `[Required] always include:\n` +
    `  **TaskRefs executed** — Exact assigned TaskRefs actually completed.\n` +
    `  **Lane** — One resolved Lane ID and its satisfied dependency/QA gates.\n` +
    `  **Changes** — Every file created/modified/deleted with a 1-line summary each.\n` +
    `  **QA results** — Outcome of each wave's QA checkpoint (pass/fail with detail).\n` +
    `[If applicable] omit if not relevant:\n` +
    `  **Deviations** — Any deviation from the plan with justification (must be reported, not hidden).\n` +
    `  **Blockers** — Steps that could not be executed and why; suggested re-direction.\n` +
    `  **Unassigned dependencies** — Required predecessor or follow-up TaskRefs not included in this dispatch.\n` +
    `Keep the report concise — bullets and short lines only. No narrative paragraphs.`,
};
